'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { HandRaiseButton } from './HandRaiseButton';
import { HandRaiseBadge } from './HandRaiseBadge';
import { HandRaiseProvider } from './HandRaiseContext';
import { HandRaiseQueueProvider } from './HandRaiseQueueContext';
import { HandRaiseQueuePanel } from './HandRaiseQueuePanel';
import { HandRaiseQueueButton } from './HandRaiseQueueButton';
import { HighlightRaisedTiles } from './HighlightRaisedTiles';
import { EmojiPickerComponent } from './EmojiPicker';
import { InjectCustomChatHeader } from './InjectCustomChatHeader';

export function InjectHandRaiseIntoBar() {
  const [container, setContainer] = React.useState<HTMLElement | null>(null);
  const [queueButtonContainer, setQueueButtonContainer] = React.useState<HTMLElement | null>(null);
  const [emojiContainer, setEmojiContainer] = React.useState<HTMLElement | null>(null);
  const [isQueuePanelOpen, setIsQueuePanelOpen] = React.useState(false);

  const insertEmojiIntoInput = React.useCallback((emoji: string) => {
    const chatInput = document.querySelector<HTMLInputElement>('.lk-chat-form-input');
    if (chatInput) {
      const start = chatInput.selectionStart || 0;
      const end = chatInput.selectionEnd || 0;
      const value = chatInput.value;

      const newValue = value.slice(0, start) + emoji + value.slice(end);

      chatInput.value = newValue;

      const inputEvent = new Event('input', { bubbles: true });
      const changeEvent = new Event('change', { bubbles: true });
      chatInput.dispatchEvent(inputEvent);
      chatInput.dispatchEvent(changeEvent);

      const newPosition = start + emoji.length;

      setTimeout(() => {
        chatInput.setSelectionRange(newPosition, newPosition);
        chatInput.focus();
      }, 0);
    }
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const injectEmojiPicker = () => {
      const chatInput = document.querySelector<HTMLInputElement>('.lk-chat-form-input');
      if (chatInput) {
        chatInput.setAttribute('placeholder', 'Digite sua mensagem...');

        const existingEmojiPicker = document.querySelector('[data-emoji-picker-injected]');
        if (existingEmojiPicker) return;

        const chatForm = chatInput.closest('.lk-chat-form');
        if (chatForm) {
          const emojiHost = document.createElement('div');
          emojiHost.setAttribute('data-emoji-picker-injected', 'true');
          emojiHost.style.position = 'absolute';
          emojiHost.style.left = '15px';
          emojiHost.style.top = '50%';
          emojiHost.style.transform = 'translateY(-50%)';
          emojiHost.style.zIndex = '10';

          const computedStyle = window.getComputedStyle(chatForm);
          if (computedStyle.position === 'static') {
            (chatForm as HTMLElement).style.position = 'relative';
          }

          chatInput.style.paddingLeft = '40px';

          chatForm.appendChild(emojiHost);
          setEmojiContainer(emojiHost);
        }
      }
    };

    const tryInsert = () => {
      const controlBar = document.querySelector(
        'body > main > div > div.lk-video-conference > div.lk-video-conference-inner > div.lk-control-bar',
      ) as HTMLElement | null;

      if (!controlBar) return false;

      // Injetar botão de levantar mão
      const existingHandRaise = controlBar.querySelector('[data-hand-raise-injected]');
      if (!existingHandRaise) {
        const screenShareBtn = controlBar.querySelector('[data-lk-source="screen_share"]');
        if (screenShareBtn) {
          const host = document.createElement('div');
          host.setAttribute('data-hand-raise-injected', 'true');
          host.style.display = 'inline-flex';
          host.style.alignItems = 'center';
          host.style.justifyContent = 'center';

          screenShareBtn.parentNode?.insertBefore(host, screenShareBtn.nextSibling);
          setContainer(host);
        }
      } else {
        setContainer(existingHandRaise as HTMLElement);
      }

      // Injetar botão da fila de mãos levantadas
      const existingQueueButton = controlBar.querySelector('[data-hand-raise-queue-injected]');
      if (!existingQueueButton) {
        const handRaiseBtn = controlBar.querySelector('[data-hand-raise-injected]');
        if (handRaiseBtn) {
          const queueHost = document.createElement('div');
          queueHost.setAttribute('data-hand-raise-queue-injected', 'true');
          queueHost.style.display = 'inline-flex';
          queueHost.style.alignItems = 'center';
          queueHost.style.justifyContent = 'center';
          queueHost.style.marginLeft = '8px';

          handRaiseBtn.parentNode?.insertBefore(queueHost, handRaiseBtn.nextSibling);
          setQueueButtonContainer(queueHost);
        }
      } else {
        setQueueButtonContainer(existingQueueButton as HTMLElement);
      }

      return true;
    };

    if (tryInsert()) {
      setTimeout(injectEmojiPicker, 100);
    }

    const observer = new MutationObserver((mutations) => {
      let shouldTryInsert = false;
      let shouldTryInjectEmoji = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              if (
                element.classList?.contains('lk-control-bar') ||
                element.querySelector?.('.lk-control-bar')
              ) {
                shouldTryInsert = true;
              }
              if (
                element.classList?.contains('lk-chat-form') ||
                element.querySelector?.('.lk-chat-form')
              ) {
                shouldTryInjectEmoji = true;
              }
            }
          });
        }
      });

      if (shouldTryInsert) {
        setTimeout(() => tryInsert(), 100);
      }

      if (shouldTryInjectEmoji) {
        setTimeout(injectEmojiPicker, 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const interval = setInterval(() => {
      if (tryInsert()) {
        clearInterval(interval);
      }
      injectEmojiPicker();
    }, 1000);

    return () => {
      observer.disconnect();
      clearInterval(interval);

      if (container && container.parentNode) {
        container.parentNode.removeChild(container);
      }

      if (queueButtonContainer && queueButtonContainer.parentNode) {
        queueButtonContainer.parentNode.removeChild(queueButtonContainer);
      }

      if (emojiContainer && emojiContainer.parentNode) {
        emojiContainer.parentNode.removeChild(emojiContainer);
      }
    };
  }, [insertEmojiIntoInput]);

  return (
    <>
      {container && createPortal(<HandRaiseButton inline />, container)}
      {queueButtonContainer &&
        createPortal(
          <HandRaiseQueueButton
            onClick={() => setIsQueuePanelOpen(!isQueuePanelOpen)}
            isActive={isQueuePanelOpen}
            inline
          />,
          queueButtonContainer,
        )}
      {emojiContainer &&
        createPortal(
          <EmojiPickerComponent onEmojiClick={insertEmojiIntoInput} />,
          emojiContainer,
        )}
      <HandRaiseBadge />
      <HighlightRaisedTiles />
      <HandRaiseQueuePanel
        isOpen={isQueuePanelOpen}
        onToggle={() => setIsQueuePanelOpen(!isQueuePanelOpen)}
      />
      <InjectCustomChatHeader />
    </>
  );
}
